import moment, { type Moment } from 'moment';

import type { ModelBranchWorkTime } from '@app/api/Branches';
import { capitalizeFirstLetter } from '@app/shared/utils';
import type { NRecords } from '@app/types';

import {
  addClientGroupIdsToEvents,
  extractTime,
  mapRecordToCalendarEvent,
} from '@pages/Calendar/utils';

import { DEFAULT_END_TIME, DEFAULT_START_TIME } from '@pages/Calendar/data';
import type { CalendarBreak, CalendarEvent } from '@pages/Calendar/data';

import type {
  IndividualCalendarDay,
  IndividualCalendarEmployeeMeta,
} from '../data';

interface MapIndividualCalendarApiDataProps {
  currentEmployeeId: number;
  employeeRecords: NRecords.ModelEmployeeRecords[];
  branchWorkTimes: ModelBranchWorkTime[];
  visibleDays: Moment[];
}

interface MapIndividualCalendarApiDataResult {
  days: IndividualCalendarDay[];
  events: CalendarEvent[];
  currentEmployeeMeta: IndividualCalendarEmployeeMeta | null;
}

const getDateKey = (value: string) => moment(value).format('YYYY-MM-DD');

const getDayName = (dayMoment: Moment) =>
  capitalizeFirstLetter(dayMoment.clone().locale('ru').format('dddd'));

const getDayDateLabel = (dayMoment: Moment) =>
  dayMoment.clone().locale('ru').format('D MMMM');

const getWorkStart = (
  schedule?: NRecords.ModelEmployeeRecords,
  branchWorkTime?: ModelBranchWorkTime
) => {
  const workSchedule = schedule?.WorkSchedules?.[0];

  if (workSchedule?.TimeS) {
    return extractTime(workSchedule.TimeS);
  }

  return branchWorkTime?.WorkTimeS
    ? extractTime(branchWorkTime.WorkTimeS)
    : DEFAULT_START_TIME;
};

const getWorkEnd = (
  schedule?: NRecords.ModelEmployeeRecords,
  branchWorkTime?: ModelBranchWorkTime
) => {
  const workSchedule = schedule?.WorkSchedules?.[0];

  if (workSchedule?.TimeE) {
    return extractTime(workSchedule.TimeE);
  }

  return branchWorkTime?.WorkTimeE
    ? extractTime(branchWorkTime.WorkTimeE)
    : DEFAULT_END_TIME;
};

const mapBreaks = (schedule?: NRecords.ModelEmployeeRecords): CalendarBreak[] =>
  (schedule?.WorkBreaks ?? []).map((workBreak) => ({
    id: workBreak.Id,
    timeStart: extractTime(workBreak.TimeS),
    timeEnd: extractTime(workBreak.TimeE),
  }));

const mapCurrentEmployeeMeta = (
  currentEmployeeId: number,
  schedule?: NRecords.ModelEmployeeRecords
): IndividualCalendarEmployeeMeta | null => {
  if (!schedule) {
    return null;
  }

  return {
    userId: currentEmployeeId,
    nameFirst: schedule.NameFirst || '',
    nameLast: schedule.NameLast || '',
    nameMiddle: schedule.NameMiddle || '',
    nameFull:
      schedule.NameFull ||
      [schedule.NameLast, schedule.NameFirst, schedule.NameMiddle]
        .filter(Boolean)
        .join(' '),
    position: schedule.Position || undefined,
    photo: schedule.Photo || undefined,
  };
};

const groupEventsByDate = (events: CalendarEvent[]) => {
  const eventsByDate = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    const dayEvents = eventsByDate.get(event.employeeId) ?? [];
    dayEvents.push(event);
    eventsByDate.set(event.employeeId, dayEvents);
  }

  return eventsByDate;
};

const getIsExecutorNonWorkingDay = (schedule?: NRecords.ModelEmployeeRecords) =>
  !schedule?.WorkSchedules?.[0];

export const mapIndividualCalendarApiData = ({
  currentEmployeeId,
  employeeRecords,
  branchWorkTimes,
  visibleDays,
}: MapIndividualCalendarApiDataProps): MapIndividualCalendarApiDataResult => {
  const schedulesByDate = new Map(
    employeeRecords
      .filter((schedule) => !schedule.IsDeleted)
      .map((schedule) => [getDateKey(schedule.Date), schedule])
  );
  const branchWorkTimesByDate = new Map(
    branchWorkTimes.map((workTime) => [getDateKey(workTime.Date), workTime])
  );

  const allEvents = addClientGroupIdsToEvents(
    visibleDays.flatMap((dayMoment) => {
      const dateKey = dayMoment.format('YYYY-MM-DD');
      const schedule = schedulesByDate.get(dateKey);

      return (schedule?.Records ?? [])
        .filter((record) => !record.IsDeleted)
        .map((record) => ({
          ...mapRecordToCalendarEvent(record),
          employeeId: dateKey,
        }));
    })
  );
  const eventsByDate = groupEventsByDate(allEvents);
  const firstSchedule = employeeRecords.find(
    (schedule) => !schedule.IsDeleted && schedule.UserId === currentEmployeeId
  );

  return {
    days: visibleDays.map((dayMoment) => {
      const dateKey = dayMoment.format('YYYY-MM-DD');
      const schedule = schedulesByDate.get(dateKey);
      const branchWorkTime = branchWorkTimesByDate.get(dateKey);
      const workStart = getWorkStart(schedule, branchWorkTime);
      const workEnd = getWorkEnd(schedule, branchWorkTime);
      const dayName = getDayName(dayMoment);
      const isExecutorNonWorkingDay = getIsExecutorNonWorkingDay(schedule);

      return {
        id: dateKey,
        date: dateKey,
        column: {
          id: dateKey,
          name: getDayDateLabel(dayMoment),
          position: dayName,
          firstName: getDayDateLabel(dayMoment),
          lastName: dayName,
          workStart,
          workEnd,
          breaks: mapBreaks(schedule),
        },
        events: eventsByDate.get(dateKey) ?? [],
        isExecutorNonWorkingDay,
        rawSchedule: schedule,
        branchWorkTime,
      };
    }),
    events: allEvents,
    currentEmployeeMeta: mapCurrentEmployeeMeta(
      currentEmployeeId,
      firstSchedule ?? employeeRecords.find((schedule) => !schedule.IsDeleted)
    ),
  };
};
